import { Card, Row, Col, Typography } from "antd";
import { useMemo } from "react";
import dayjs from "dayjs";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, ArcElement, PointElement, Tooltip, Legend } from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, ArcElement, PointElement, Tooltip, Legend);
const { Text } = Typography;
const COLORS = {
  primary: "#328BC7",
  darkBlue: "#042956",
  danger: "#DC001A",
  warning: "#F3A200",
  success: "#52C41A",
  peach: "#FFDAB9",
  white: "#FFFFFF",
  textDark: "#042956",
  textLight: "#FFFFFF"
};



export default function DashboardCharts({ stock = [], movimientos = [] }) {

  const topStockProductos = useMemo(() => {
    if (!stock.length) return { labels: [], datasets: [] };
    const sorted = [...stock]
      .sort(
        (a, b) =>
          Number(b.stock_disponible || 0) -
          Number(a.stock_disponible || 0)
      )
      .slice(0, 8);
    return {
      labels: sorted.map(p =>
        (p.nombre_producto || "Producto").slice(0, 22) + "..."
      ),
      datasets: [
        {
          label: "Stock disponible",
          data: sorted.map(p =>
            Number(p.stock_disponible || 0)
          ),
          backgroundColor: COLORS.primary,
          borderRadius: 6,
          barThickness: 18,
          datalabels: {
            color: "#FFFFFF",
            anchor: "center",
            align: "center",
            font: {
              weight: "bold",
              size: 12
            }
          }
        }
      ]
    };
  }, [stock]);

  const ESTADOS_STOCK = {
    NORMAL: {
      label: "Disponible",
      descripcion: "Hay suficiente stock disponible para las entregas.",
      color: COLORS.primary
    },
    BAJO: {
      label: "Parcial",
      descripcion: "El stock es limitado y puede no cubrir todas las entregas.",
      color: COLORS.peach
    },
    AGOTADO: {
      label: "Agotado",
      descripcion: "No hay unidades disponibles en inventario.",
      color: COLORS.success
    },
    CRITICO: {
      label: "Crítico",
      descripcion: "El stock está por agotarse y requiere reposición urgente.",
      color: COLORS.danger
    }
  };

  const distribucionStock = useMemo(() => {

    if (!stock.length) return { labels: [], datasets: [] };

    const estados = {};

    stock.forEach(p => {

      const estado = (p.estado_stock || "NORMAL").toUpperCase();

      if (!estados[estado]) estados[estado] = 0;

      estados[estado]++;

    });

    const labels = Object.keys(estados).map(e => ESTADOS_STOCK[e]?.label || e);

    const colors = Object.keys(estados).map(e => ESTADOS_STOCK[e]?.color || COLORS.primary);

    return {
      labels,
      datasets: [
        {
          data: Object.values(estados),
          backgroundColor: colors,
          borderWidth: 0,
          datalabels: {
            color: "#FFFFFF",
            font: {
              weight: "bold",
              size: 14
            }
          }
        }
      ]
    };

  }, [stock]);

  const entregasDepartamento = useMemo(() => {
    if (!movimientos.length) return { labels: [], datasets: [] };
    const map = {};
    movimientos.forEach(m => {
      const dept =
        m.departamento ||
        m.nombre_departamento ||
        "Sin dato";
      const cantidad = Number(
        m.cantidad_entregada ||
        m.cantidad ||
        m.unidades ||
        0
      );
      map[dept] = (map[dept] || 0) + cantidad;
    });
    const top = Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    return {
      labels: top.map(i => i[0]),
      datasets: [
        {
          label: "Unidades entregadas",
          data: top.map(i => i[1]),
          backgroundColor: COLORS.darkBlue,
          borderRadius: 6,
          barThickness: 18,
          datalabels: {
            color: "#FFFFFF",
            anchor: "center",
            align: "center",
            font: {
              weight: "bold",
              size: 12
            },
            formatter: (value) =>
              new Intl.NumberFormat("es-CO").format(value)
          }
        }
      ]
    };
  }, [movimientos]);

  const entregasMes = useMemo(() => {
    if (!movimientos.length) return { labels: [], datasets: [] };
    const map = {};
    movimientos.forEach(m => {
      const fecha =
        m.fecha_entrega ||
        m.fecha ||
        m.created_at;
      if (!fecha) return;
      const mes = dayjs(fecha).format("YYYY-MM");
      const cantidad = Number(
        m.cantidad_entregada ||
        m.cantidad ||
        0
      );
      map[mes] = (map[mes] || 0) + cantidad;
    });
    const sorted = Object.entries(map)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]));
    return {
      labels: sorted.map(i => i[0]),
      datasets: [
        {
          label: "Entregas",
          data: sorted.map(i => i[1]),
          borderColor: COLORS.primary,
          backgroundColor: "rgba(50,139,199,0.15)",
          tension: 0.35,
          pointRadius: 4,
          pointBackgroundColor: COLORS.darkBlue
        }
      ]
    };
  }, [movimientos]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: {
        grid: { color: "#f0f0f0" },
        ticks: { color: COLORS.textDark }
      },
      y: {
        grid: { display: false },
        ticks: { color: COLORS.textDark }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {

      legend: {
        position: "right",
        labels: {
          color: COLORS.textDark
        }
      },

      tooltip: {
        callbacks: {

          label: function (context) {

            const label = context.label;
            const value = context.raw;

            const estado = Object.values(ESTADOS_STOCK).find(e => e.label === label);

            return [
              `${label}: ${value} productos`,
              estado?.descripcion || ""
            ];

          }

        }
      }

    }
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: COLORS.textDark
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: COLORS.textDark }
      },
      y: {
        grid: { color: "#f0f0f0" },
        ticks: { color: COLORS.textDark }
      }
    }
  };

  return (
    <Row gutter={[16, 16]} style={{ marginTop: 24, marginBottom: 24 }}>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Stock por Producto</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Top disponibles</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 240 }}>
            <Bar
              data={topStockProductos}
              options={barOptions}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Estado del Stock</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Distribución inventario</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 240, padding: 12 }}>
            <Doughnut
              data={distribucionStock}
              options={pieOptions}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Entregas por Departamento</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Top departamentos</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 240 }}>
            <Bar
              data={entregasDepartamento}
              options={barOptions}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          title={<span style={{ color: "#FFFFFF" }}>Tendencia de Entregas</span>}
          extra={<Text style={{ color: "#FFFFFF" }}>Evolución mensual</Text>}
          style={{
            borderRadius: 10,
            border: "1px solid #f0f0f0"
          }}
          styles={{
            header: {
              background: "#042956",
              borderBottom: "none"
            }
          }}
        >
          <div style={{ height: 240 }}>
            <Line
              data={entregasMes}
              options={lineOptions}
            />
          </div>
        </Card>
      </Col>
    </Row>
  );

}